import React, { useState, useEffect, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Select, Checkbox, Popconfirm,
  Button,
} from 'antd';
import Helpers from '@Apis/helpers';
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import DailyRunRateActions from '@Actions/inventory/dailyRunRateActions';
import StockMasterActions from '@Actions/inventory/stockMasterActions';
import SelectDepartment from '../../../../Common/SelectDepartment';
import ScheduledReport from '../../../../ReportsV2/ScheduledReport';
import './style.scss';
import PRZSelect from '../../../../Common/UI/PRZSelect';

const DailyRunRateForm = ({
  user, getProductList, productLists, getProductListLoading, createDailyRunRate, createDailyRunRateLoading, updateDailyRunRate, updateDailyRunRateLoading, callback, skuListIds, selectedDRR, drrId, isUpdating, getDailyRunRateById, getDailyRunRateByIdLoading, getDailyRunRateByIdSuccess
}) => {
  const [skuListId, setSkuListId] = useState(null);
  const [entitiesToTrack, setEntitiesToTrack] = useState([]);
  const [daysToTrack, setDaysToTrack] = useState(30);
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [includeExpiredStock, setIncludeExpiredStock] = useState(false);
  const [includeRejectedStock, setIncludeRejectedStock] = useState(false);
  const [calculateDrrFromMfq, setCalculateDrrFromMfq] = useState(false);
  const [enableNotification, setEnableNotification] = useState(false);
  const [runningLowNotification, setRunningLowNotification] = useState(false);
  const [outOfStockNotification, setOutOfStockNotification] = useState(false);
  const [notificationRecipients, setNotificationRecipients] = useState([]);
  const [schedule, setSchedule] = useState('');
  const [targetDaysOfStock, setTargetDaysOfStock] = useState(7);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const transactionEntities = [
    {
      id: 1,
      name: 'Invoice',
      value: 'invoice',
    },
    {
      id: 2,
      name: 'Stock Transfer',
      value: 'stock_transfer',
    },
    {
      id: 3,
      name: 'Outward Adjustment',
      value: 'stock_adjustment',
    },
    {
      id: 4,
      name: 'Raw Material Usage',
      value: 'manufacturing_order',
    },
  ];
  const transactionEntitiesOptions = transactionEntities.map((item) => ({
    label: item.name,
    value: item.value,
  }));

  useEffect(() => {
    getProductList({
      tenantId: Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ)?.join(','),
      keyword: Helpers.getValueFromUrl('keyword'),
    });

    if (selectedDRR) {
      setSkuListId(selectedDRR?.sku_list_info?.sku_list_id);
      setEntitiesToTrack(selectedDRR?.entities_to_track);
      setDaysToTrack(selectedDRR?.days_to_track);
      setSelectedDepartment(selectedDRR?.departments_to_track);
      setIncludeExpiredStock(selectedDRR?.include_expired_stock);
      setIncludeRejectedStock(selectedDRR?.include_rejected_stock);
      setCalculateDrrFromMfq(selectedDRR?.calculate_drr_from_mfq);
      setEnableNotification(selectedDRR?.enable_notification);
      setRunningLowNotification(selectedDRR?.running_low_notification);
      setOutOfStockNotification(selectedDRR?.out_of_stock_notification);
      setNotificationRecipients(selectedDRR?.notification_recipients);
      setTargetDaysOfStock(selectedDRR?.target_days_of_stock);

      let scheduleConfig = selectedDRR?.schedule_configurations;
      const scheduleData = {
        isScheduledReport: true,
        selectedTime: scheduleConfig?.time,
        selectedFrequency: scheduleConfig?.type,
        selectedDate: scheduleConfig?.date,
        selectedDays: scheduleConfig?.days,
      };

      setSchedule(scheduleData);
    }
  }, []);

  const isScheduleValid = () => {
    if(enableNotification) { 
      if (schedule?.selectedFrequency === 'DAILY' && !schedule?.selectedTime) return false;
      if (schedule?.selectedFrequency === 'WEEKLY' && !schedule?.selectedTime && !schedule?.selectedDays.length) return false;
      if (schedule?.selectedFrequency === 'MONTHLY' && !schedule?.selectedTime && !schedule?.selectedDate) return false;
    }
    return true;
  };

  const dataValid = () => {
    if (!skuListId || !daysToTrack || !entitiesToTrack?.length || !selectedDepartment?.length
      || (enableNotification && (!outOfStockNotification && !runningLowNotification || !notificationRecipients?.length))) {
      return false;
    }
    return true;
  };

  const handleInventoryDaysTracking = () => {
    setFormSubmitted(true);
    if (dataValid() && isScheduleValid()) {
      const payload = {
        sku_list_id: skuListId,
        tenant_id: user?.tenant_info?.tenant_id,
        days_to_track: daysToTrack,
        entities_to_track: entitiesToTrack,
        departments_to_track: selectedDepartment,
        include_expired_stock: includeExpiredStock,
        include_rejected_stock: includeRejectedStock,
        calculate_drr_from_mfq: calculateDrrFromMfq,
        enable_notification: enableNotification,
        running_low_notification: runningLowNotification,
        out_of_stock_notification: outOfStockNotification,
        notification_recipients: notificationRecipients,
        schedule_configurations: {
          "time": schedule?.selectedTime,
          "type": schedule?.selectedFrequency,
          "specific_date": schedule?.selectedDate,
          "days": schedule?.selectedDays,
          "date": schedule?.selectedDate ? schedule?.selectedFrequency === 'MONTHLY' ? schedule?.selectedDate.date() : dayjs(schedule?.selectedDate).format('DD-MM-YYYY') : null,
        },
        job_name: "SEND_DAILY_RUN_RATE_SKU_STATUS",
        target_days_of_stock: targetDaysOfStock,
      };

      if (selectedDRR) {
        payload.drr_id = selectedDRR?.drr_id;

        updateDailyRunRate(payload, () => {
          callback();
        });
      } else {
        createDailyRunRate(payload, () => {
          callback();
        });
      }

    }
  };

  return (
    <Fragment>
      <div className="drr-form__wrapper">
        <div className="ant-row">

          <div className="ant-col-md-24">
            <div className="orgInputContainer">
              <div className="orgFormLabel">
                Select product group
                <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
              </div>
              <div className="add-seller__form-type">
                <PRZSelect
                  disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                  value={skuListId}
                  showSearch
                  onChange={(value) => {
                    setSkuListId(value);
                  }}
                  showError={formSubmitted && !skuListId}
                  errorName=" a product group"
                  errorClassName="input-error"
                >
                  {productLists?.sku_lists?.map((productList) => (!skuListIds?.includes(productList.sku_list_id) ? <Option key={productList.sku_list_id} value={productList.sku_list_id}>{productList.list_name}</Option> : ''))}
                </PRZSelect>
              </div>
            </div>
          </div>

          <div className="ant-col-md-24">
            <div className="orgInputContainer">
              <div className="orgFormLabel">
                Select outwards stock entries to be used for tracking daily consumption
                <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
              </div>
              <div className="add-seller__form-type">
                <PRZSelect
                  disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                  value={entitiesToTrack}
                  onChange={(value) => {
                    setEntitiesToTrack(value);
                  }}
                  mode="multiple"
                  maxTagCount="responsive"
                  options={transactionEntitiesOptions}
                  showError={formSubmitted && !entitiesToTrack?.length}
                  errorName=" outward transaction"
                  errorClassName="input-error"
                />
              </div>
            </div>
          </div>

          <div className="ant-col-md-24">
            <H3FormInput
              name={daysToTrack < 7 ? 'least transaction days can be 7days' : 'a valid transaction days'}
              type="number"
              containerClassName="orgInputContainer"
              labelClassName="orgFormLabel"
              inputClassName="orgFormInput"
              placeholder=""
              onChange={(event) => setDaysToTrack(event.target.value)}
              disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
              value={daysToTrack}
              required
              showError={formSubmitted && (!daysToTrack || daysToTrack < 7)}
              label="Number of days to track"
            />
          </div>

          <div className="ant-col-md-24">
            <SelectDepartment
              title="Which department's stock you want to track"
              selectedDepartment={selectedDepartment}
              noDropdownAlign
              onChange={(value) => {
                setSelectedDepartment(value);
              }}
              isMultiple
              emptyNotAllowed
              showSearch
              disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
              tenantId={selectedDRR?.tenant_id ?? user?.tenant_info?.tenant_id}
            />
          </div>
          {formSubmitted && !selectedDepartment?.length && (<div className="input-error">*Please select department</div>)}

          <div className="ant-col-md-24">
            <div className="form__input-row" style={{ marginTop: '15px' }}>
              <Checkbox
                disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                checked={includeExpiredStock}
                onChange={() => {
                  setIncludeExpiredStock(!includeExpiredStock);
                }}
              />
              <span
                style={{
                  fontWeight: '500',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                Include expired stock in consumption
              </span>
            </div>
          </div>

          <div className="ant-col-md-24">
            <div className="form__input-row" style={{ marginTop: '15px' }}>
              <Checkbox
                disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                checked={includeRejectedStock}
                onChange={() => {
                  setIncludeRejectedStock(!includeRejectedStock);
                }}
              />
              <span
                style={{
                  fontWeight: '500',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                Include bad quality stock in consumption
              </span>
            </div>
          </div>

          <div className="ant-col-md-24">
            <div className="form__input-row" style={{ marginTop: '15px' }}>
              <Checkbox
                disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                checked={calculateDrrFromMfq}
                onChange={() => {
                  setCalculateDrrFromMfq(!calculateDrrFromMfq);
                }}
              />
              <span
                style={{
                  fontWeight: '500',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                Calculate stock status from minimum floor quantity when no transaction found
              </span>
            </div>
          </div>

          <div className="ant-col-md-24">
            <div className="form__input-row" style={{ marginTop: '15px' }}>
              <Checkbox
                disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                checked={enableNotification}
                onChange={() => {
                  setEnableNotification(!enableNotification);
                }}
              />
              <span
                style={{
                  fontWeight: '500',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                Enable email notification for stock status
              </span>
            </div>
          </div>

          {enableNotification && (
            <Fragment>
              <div className="ant-col-md-24">
                <div className="mg-left-20">
                  <div className="form__input-row" style={{ marginTop: '15px' }}>
                    <Checkbox
                      disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                      checked={runningLowNotification}
                      onChange={() => {
                        setRunningLowNotification(!runningLowNotification);
                      }}
                    />
                    <span
                      style={{
                        fontWeight: '500',
                        fontSize: '12px',
                        marginLeft: '5px',
                      }}
                    >
                      Running Low Stock
                    </span>
                  </div>
                  <div className="form__input-row" style={{ marginTop: '15px' }}>
                    <Checkbox
                      disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                      checked={outOfStockNotification}
                      onChange={() => {
                        setOutOfStockNotification(!outOfStockNotification);
                      }}
                    />
                    <span
                      style={{
                        fontWeight: '500',
                        fontSize: '12px',
                        marginLeft: '5px',
                      }}
                    >
                      Out of Stock
                    </span>
                  </div>
                </div>
              </div>
              {formSubmitted && !(runningLowNotification || outOfStockNotification) && (<div className="input-error">*Please check notification type</div>)}

              {(runningLowNotification || outOfStockNotification) && (
                <div className="ant-col-md-24">
                  <div className="orgInputContainer" style={{ marginBottom: '0px', marginTop: '15px' }}>
                    <div className="orgFormLabel">
                      Add recipients for notification
                      <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </div>
                    <PRZSelect
                      className={(formSubmitted && enableNotification && !notificationRecipients?.length) ? 'error' : ''}
                      mode="tags"
                      value={notificationRecipients}
                      filterOption={false}
                      maxTagCount="responsive"
                      onChange={(value) => {
                        const recipients = [];
                        for (let i = 0; i < value?.length; i++) {
                          if (Helpers.validateEmail(value[i])) {
                            recipients.push(value[i]);
                          }
                        }
                        setNotificationRecipients(recipients);
                      }}
                      disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                      showError={formSubmitted && (enableNotification && (!outOfStockNotification && !runningLowNotification || !notificationRecipients?.length))}
                      errorName="*Please add recipients"
                      errorClassName="input-error"
                    />
                  </div>


                  <div className="">
                    <ScheduledReport
                      allowedDurations={['DAILY', 'WEEKLY', 'MONTHLY']}
                      setScheduledReportData={setSchedule}
                      alwaysOn
                      disabled={!notificationRecipients}
                      schedulePrefillData={schedule}
                    />
                    {formSubmitted && !isScheduleValid() && <div className="input-error">Please select a valid schedule</div>}
                  </div>
                </div>
              )}
            </Fragment>
          )}

          <div className="ant-col-md-24">
            <div style={{ marginTop: '15px' }}>
              <H3FormInput
                name="a valid days of stock required"
                type="number"
                containerClassName="orgInputContainer"
                labelClassName="orgFormLabel"
                inputClassName="orgFormInput"
                placeholder=""
                onChange={(event) => setTargetDaysOfStock(event.target.value)}
                disabled={getProductListLoading || createDailyRunRateLoading || updateDailyRunRateLoading}
                value={targetDaysOfStock}
                required
                showError={formSubmitted && !targetDaysOfStock}
                label="What is the target days of stock that you want to maintain?"
              />
            </div>
          </div>

        </div>
      </div>
      <div className="custom-drawer__footer">
        <div className="ant-col-md-6" style={{ marginTop: '0.5rem' }}>
          <div className="indent-barcode__wrapper" style={{ display: 'flex' }}>
            <Button
              type="primary"
              onClick={() => handleInventoryDaysTracking()}
              loading={getProductListLoading}
              disabled={createDailyRunRateLoading || updateDailyRunRateLoading}
            >
              {selectedDRR ? 'Update' : 'Create'}
            </Button>
            {false && (
              <Popconfirm
                placement="topRight"
                title="This feature is not accessible within your current plan to use this feature contact us."
                onConfirm={() => window.Intercom('showNewMessage')}
                okText="Contact Us"
                cancelText="Cancel"
              >
                <img
                  className="barcode-restrict"
                  src={Crown}
                  alt="premium"
                  style={{
                    marginLeft: '8px',
                    marginTop: '5px',
                  }}
                />
              </Popconfirm>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, StockMasterReducers, DailyRunRateReducers,
}) => ({
  user: UserReducers.user,
  productLists: StockMasterReducers.productLists,
  getProductListLoading: StockMasterReducers.getProductListLoading,
  createDailyRunRateLoading: DailyRunRateReducers.createDailyRunRateLoading,
  updateDailyRunRate: DailyRunRateReducers.updateDailyRunRate,
});

const mapDispatchToProps = (dispatch) => ({
  createDailyRunRate: (payload, callback) => dispatch(DailyRunRateActions.createDailyRunRate(payload, callback)),
  updateDailyRunRate: (payload, callback) => dispatch(DailyRunRateActions.updateDailyRunRate(payload, callback)),
  getProductList: (payload, callback) => dispatch(StockMasterActions.getProductList(payload, callback)),
});


export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DailyRunRateForm));
